import { css } from '@emotion/react'
import { graphql, useStaticQuery } from 'gatsby'
import {
  ComponentPropsWithoutRef,
  Dispatch,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'

import { DatoStructuredText, FilterDropdown } from '@/features/common/'
import { DatoStructuredTextLink } from '@/features/dato-link'
import { mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

interface FilterProps {
  category?: string | null
  division?: string | null
}

interface Props extends ComponentPropsWithoutRef<'nav'> {
  filters: FilterProps
  setFilters: Dispatch<SetStateAction<FilterProps>>
}

export const NewsFilters = ({
  filters,
  setFilters,
  ...props
}: Props): JSX.Element => {
  const {
    datoCmsNewsPage,
    allDatoCmsDivisionPage: { nodes: divisions },
    allDatoCmsNewsArticleCategory: { nodes: categories },
  } = useStaticQuery<Queries.NewsFiltersQuery>(graphql`
    query NewsFilters {
      datoCmsNewsPage {
        bulletinTextAndLink {
          value
          links {
            __typename
            id: originalId
            ... on DatoCmsNewsletterPage {
              slug
            }
          }
        }
      }
      allDatoCmsDivisionPage(sort: { divisionName: ASC }) {
        nodes {
          divisionName
        }
      }
      allDatoCmsNewsArticleCategory(sort: { position: ASC }) {
        nodes {
          name
          dropdownNameOverride
        }
      }
    }
  `)

  const searchParams = useRef<URLSearchParams>()
  useLayoutEffect(() => {
    searchParams.current = new URLSearchParams(window.location.search)
    setFilters({
      category: searchParams.current?.get('category'),
      division:
        searchParams.current?.get('category') === 'Alumnae News'
          ? null
          : searchParams.current?.get('division'),
    })
  }, [setFilters])

  useEffect(() => {
    filters.category
      ? searchParams.current?.set('category', filters.category)
      : searchParams.current?.delete('category')
    filters.division
      ? searchParams.current?.set('division', filters.division)
      : searchParams.current?.delete('division')
    window.history.replaceState(
      {},
      '',
      decodeURIComponent(
        `${window.location.pathname}${
          searchParams.current?.size && searchParams.current?.size > 0
            ? `?${searchParams.current}`
            : ''
        }`
      )
    )
  }, [filters])

  const styles = {
    container: css`
      display: flex;
      column-gap: 2em;
      row-gap: 1em;
      padding: var(--row-36) var(--margin);
      background: ${colors.gray95};
      flex-wrap: wrap;
      max-width: 100vw;
      box-sizing: border-box;
      justify-content: flex-start;
    `,
    bulletinText: css`
      flex: 1;
      text-align: right;
      font-size: var(--fs-18);
      font-style: italic;
      align-self: center;
      color: ${colors.gray45};
      line-height: 1.25;
      a {
        color: ${colors.gray30};
        display: inline-block;
        @media (hover: hover) {
          &:hover {
            color: ${colors.red};
          }
        }
      }
      p {
        margin: 0;
      }
      ${mq().ms} {
        flex: none;
        text-align: left;
        margin-top: 1em;
        max-width: 100%;
      }
    `,
  }
  return (
    <nav
      css={styles.container}
      {...props}
    >
      <FilterDropdown
        options={[
          { label: 'All Articles', value: '' },
          ...categories.map(category => ({
            label: category.dropdownNameOverride || category.name,
            value: category.name,
          })),
        ]}
        onChange={e => {
          setFilters(prev => ({
            ...prev,
            category: e.target.value || null,
          }))
        }}
        value={filters.category || ''}
      />
      {filters.category !== 'Alumnae News' && (
        <FilterDropdown
          options={[
            { label: 'All Divisions', value: '' },
            ...divisions.map(({ divisionName }) => ({
              label: divisionName,
              value: divisionName,
            })),
          ]}
          onChange={e => {
            setFilters(prev => ({
              ...prev,
              division: e.target.value || null,
            }))
          }}
        />
      )}
      <div css={styles.bulletinText}>
        <DatoStructuredText
          data={datoCmsNewsPage?.bulletinTextAndLink}
          renderLinkToRecord={({
            record,
            children,
            transformedMeta,
          }) => (
            <DatoStructuredTextLink
              data={record}
              {...transformedMeta}
            >
              {children}
            </DatoStructuredTextLink>
          )}
        />
      </div>
    </nav>
  )
}
